// src/pages/DebugScreen.js
// ─────────────────────────────────────────────────────────────────────────────
// Accessible at /debug — shows exact auth state so you can diagnose issues
// without needing browser console access.
// REMOVE THIS FILE before going fully public (or keep it, it shows nothing
// sensitive — just tells you what the app knows about the current user).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';

export default function DebugScreen() {
  const { user, profile, isAdmin, isStaff, authLoading, lang, country, displayCurrency, refreshProfile } = useApp();
  const [refreshing, setRefreshing] = useState(false);
  const [sqlResult, setSqlResult] = useState(null);
  const [sqlRunning, setSqlRunning] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshProfile();
    setRefreshing(false);
  };

  const checkDatabase = async () => {
    setSqlRunning(true);
    try {
      // Check if profiles table is accessible
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, role')
        .eq('id', user?.id)
        .maybeSingle();
      setSqlResult({ data, error: error?.message || null });
    } catch (e) {
      setSqlResult({ error: e.message });
    }
    setSqlRunning(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const row = (label, value, good) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
      <span style={{ fontSize: '.85rem', color: '#777', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.08em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: '.88rem', fontWeight: 700, color: good === true ? '#4cca74' : good === false ? '#e05050' : '#f4f4f4', fontFamily: 'monospace' }}>
        {String(value ?? 'null')}
      </span>
    </div>
  );

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px' }}>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', letterSpacing: '.06em', color: '#4cca74', marginBottom: 4 }}>
        Debug Panel
      </div>
      <p style={{ color: '#555', fontSize: '.82rem', marginBottom: 32 }}>
        This page shows exactly what the app knows about your current session. Use it to diagnose login and admin issues.
      </p>

      {/* Auth State */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', padding: '20px', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 12 }}>
          Auth State
        </div>
        {row('authLoading',  authLoading,      authLoading === false)}
        {row('user.id',      user?.id,          !!user)}
        {row('user.email',   user?.email,       !!user)}
        {row('user logged in', !!user,          !!user)}
      </div>

      {/* Profile */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', padding: '20px', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 12 }}>
          Profile (from Supabase profiles table)
        </div>
        {row('profile exists',  !!profile,         !!profile)}
        {row('profile.id',      profile?.id)}
        {row('profile.email',   profile?.email)}
        {row('profile.role',    profile?.role,      profile?.role === 'admin' || profile?.role === 'staff')}
        {row('profile.loyalty_tier', profile?.loyalty_tier)}
        {row('profile.loyalty_points', profile?.loyalty_points)}
      </div>

      {/* Admin Status */}
      <div style={{ background: profile && !isAdmin ? 'rgba(224,80,80,.06)' : 'rgba(76,202,116,.06)', borderRadius: 16, border: `1px solid ${profile && !isAdmin ? 'rgba(224,80,80,.2)' : 'rgba(76,202,116,.2)'}`, padding: '20px', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 12 }}>
          Admin Status
        </div>
        {row('isAdmin', isAdmin, isAdmin)}
        {row('isStaff', isStaff, isStaff)}
        {!isAdmin && profile && (
          <div style={{ marginTop: 12, padding: '12px', background: 'rgba(224,80,80,.08)', borderRadius: 10, fontSize: '.82rem', color: '#e05050', lineHeight: 1.8 }}>
            ⚠ Your role is "{profile?.role || 'unknown'}". Run this in Supabase SQL editor to fix:<br />
            <code style={{ fontSize: '.78rem', color: '#f4f4f4', fontFamily: 'monospace' }}>
              UPDATE public.profiles SET role = 'admin' WHERE email = '{user?.email}';
            </code>
            <br />Then click "Refresh Profile" below.
          </div>
        )}
        {!profile && !authLoading && (
          <div style={{ marginTop: 12, padding: '12px', background: 'rgba(240,165,0,.08)', borderRadius: 10, fontSize: '.82rem', color: '#f0a500', lineHeight: 1.8 }}>
            ⚠ No profile found. This usually means you are not logged in, or the profiles table has no row for your account. Log in first, then come back here.
          </div>
        )}
      </div>

      {/* Locale */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', padding: '20px', marginBottom: 24 }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 12 }}>
          Locale
        </div>
        {row('lang',            lang)}
        {row('country',         country)}
        {row('displayCurrency', displayCurrency)}
      </div>

      {/* Database check */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', padding: '20px', marginBottom: 24 }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 12 }}>
          Direct Database Check
        </div>
        <p style={{ fontSize: '.8rem', color: '#666', marginBottom: 12 }}>
          Queries Supabase directly to check if your profile row is readable. If this returns data but profile above is null, there is a timing issue. If this returns an error, RLS is blocking the read.
        </p>
        <button onClick={checkDatabase} disabled={!user || sqlRunning} style={{ background: 'none', border: '1px solid rgba(76,202,116,.3)', borderRadius: 20, padding: '8px 20px', color: '#4cca74', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.8rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', marginBottom: 12 }}>
          {sqlRunning ? 'Checking…' : 'Run Check'}
        </button>
        {sqlResult && (
          <pre style={{ background: '#0a0a0a', borderRadius: 8, padding: 12, fontSize: '.75rem', color: sqlResult.error ? '#e05050' : '#4cca74', overflow: 'auto', margin: 0 }}>
            {JSON.stringify(sqlResult, null, 2)}
          </pre>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={handleRefresh} disabled={refreshing || !user} style={{ background: '#4cca74', border: 'none', borderRadius: 25, padding: '10px 24px', color: '#000', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
          {refreshing ? 'Refreshing…' : 'Refresh Profile'}
        </button>
        <a href="/admin" style={{ background: 'rgba(76,202,116,.1)', border: '1px solid rgba(76,202,116,.3)', borderRadius: 25, padding: '10px 24px', color: '#4cca74', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
          Go to Admin →
        </a>
        <a href="/login" style={{ background: 'none', border: '1px solid #333', borderRadius: 25, padding: '10px 24px', color: '#888', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
          Go to Login
        </a>
        {user && (
          <button onClick={handleLogout} style={{ background: 'none', border: '1px solid rgba(224,80,80,.3)', borderRadius: 25, padding: '10px 24px', color: '#e05050', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
            Log Out
          </button>
        )}
      </div>
    </div>
  );
}
